import { useMemo, useState } from "react";
import SEO from "@/components/SEO";
import { PageHero, Section, SectionHead, FAQ, NumberedList } from "@/components/sections";
import { Reveal } from "@/components/Reveal";
import { CTALink, Disclaimer } from "@/components/ui-kit";
import { Slider } from "@/components/ui/slider";
import LeadForm from "@/components/LeadForm";
import { ShieldAlert, ArrowDownUp, Gauge } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const NAVY = "#112C50";
const ORANGE = "#F1771E";

// Synthetic, clearly-illustrative market path (index level) across 12 periods.
const marketPath = [100, 108, 118, 124, 120, 105, 88, 82, 95, 110, 122, 132];

const framework = [
  { title: "Detect regime shifts", body: "The engine monitors trend and volatility signals to flag when markets are stretched near a top or capitulating near a bottom." },
  { title: "Safeguard before crisis", body: "As risk rises, DAAE rules move up to 20% of the portfolio from equity into debt — trimming exposure before, not after, a drawdown." },
  { title: "Deploy at the bottom", body: "As markets bottom and signals turn, the same 20% band rotates from debt back into equity — buying when others are fearful." },
  { title: "Rebalance with discipline", body: "Shifts are rules-based and bounded (a 20% band), removing emotion and keeping the core allocation intact through cycles." },
];

const faq = [
  { q: "What is DAAE?", a: "DAAE — the Dynamic Asset Allocation Engine — is a proprietary, rules-based framework by Rao Enterprises that tactically shifts up to 20% of a portfolio between equity and debt to manage risk through market cycles and pursue potential alpha." },
  { q: "Does DAAE guarantee higher returns?", a: "No. DAAE is a risk-management and allocation discipline, not a return guarantee. It aims to reduce the impact of large drawdowns and re-enter systematically. All outcomes are potential and subject to market risk." },
  { q: "How much of the portfolio moves?", a: "Up to 20% shifts between equity and debt based on the engine's signals; the remaining core allocation stays invested to your long-term plan." },
  { q: "Is the chart on this page real performance?", a: "No. All visuals here are illustrative simulations on a synthetic market path to explain the mechanism. They are not historical or expected returns." },
];

function DAAEViz() {
  const [band, setBand] = useState(20);
  const data = useMemo(() => {
    // Illustrative: static 100% equity vs DAAE band-managed path.
    let staticVal = 100;
    let daaeVal = 100;
    return marketPath.map((lvl, i) => {
      const prev = i === 0 ? marketPath[0] : marketPath[i - 1];
      const ret = lvl / prev - 1;
      // Simple illustrative rule: reduce equity exposure after strong runs, add after falls.
      const trend = i >= 2 ? marketPath[i - 1] / marketPath[i - 2] - 1 : 0;
      let equityWeight = 1;
      if (trend > 0.03) equityWeight = 1 - band / 100; // de-risked near tops
      if (trend < -0.05) equityWeight = 1; // fully invested near bottoms
      staticVal = staticVal * (1 + ret);
      daaeVal = daaeVal * (1 + ret * equityWeight + (1 - equityWeight) * 0.005);
      return {
        period: "P" + i,
        "Static 100% Equity": Math.round(staticVal),
        "DAAE Managed": Math.round(daaeVal),
      };
    });
  }, [band]);

  return (
    <div className="bg-white border border-navy/10 p-8 md:p-10" data-testid="daae-viz">
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <div className="font-sans text-xs uppercase tracking-[0.2em] text-slate mb-4">Tactical band</div>
          <div className="flex justify-between items-baseline mb-3">
            <span className="font-sans text-sm text-offblack">Max equity ⇄ debt shift</span>
            <span className="font-data text-lg text-navy">{band}%</span>
          </div>
          <Slider value={[band]} min={0} max={30} step={5} onValueChange={(v) => setBand(v[0])} data-testid="daae-band-slider" />
          <p className="mt-6 font-data text-xs text-slate leading-relaxed">
            DAAE's default band is 20%. Drag to see how a wider or narrower band changes the illustrative path.
          </p>
        </div>
        <div className="lg:col-span-2 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ left: -12, right: 4, top: 5, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#00000010" vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#5B6672" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#5B6672" }} tickLine={false} axisLine={false} width={40} />
              <Tooltip contentStyle={{ fontFamily: "IBM Plex Mono", fontSize: 12, borderRadius: 0 }} />
              <Legend wrapperStyle={{ fontFamily: "Inter", fontSize: 12 }} />
              <Line type="monotone" dataKey="Static 100% Equity" stroke={NAVY} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="DAAE Managed" stroke={ORANGE} strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <Disclaimer className="mt-6">
        *Illustrative simulation on a synthetic market path to explain the mechanism only. Not historical performance, not a forecast, and not a guarantee of alpha. Investments are subject to market risk.
      </Disclaimer>
    </div>
  );
}

export default function DAAE() {
  return (
    <div>
      <SEO path="/products/daae" title="DAAE — Dynamic Asset Allocation Engine" description="DAAE, a proprietary rules-based Dynamic Asset Allocation Engine by Rao Enterprises, tactically shifts up to 20% between equity and debt to manage drawdowns and pursue potential alpha. Illustrative only." faq={faq} />
      <PageHero
        dark
        label="Products · Flagship"
        title="DAAE — the Dynamic Asset Allocation Engine"
        intro="A proprietary, rules-based engine by Rao Enterprises that safeguards capital before crises and re-enters near bottoms — shifting up to 20% between equity and debt in pursuit of potential alpha."
        breadcrumbs={[{ name: "Home", to: "/" }, { name: "Products" }, { name: "DAAE" }]}
      />

      <Section>
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {[
            { icon: ShieldAlert, t: "Safeguard first", d: "De-risk toward debt as markets get stretched — before drawdowns, not after." },
            { icon: ArrowDownUp, t: "20% tactical band", d: "A bounded, disciplined shift between equity and debt keeps the core plan intact." },
            { icon: Gauge, t: "Systematic, not emotional", d: "Rules replace guesswork, aiming to buy fear and trim greed across cycles." },
          ].map((c, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="h-full bg-navy text-white p-8 border border-navy/10">
                <c.icon className="w-8 h-8 text-orange" strokeWidth={1.25} />
                <h3 className="mt-6 font-serif text-xl">{c.t}</h3>
                <p className="mt-3 font-sans text-sm text-white/60 leading-relaxed">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <SectionHead label="See the mechanism" title="How the 20% band behaves in an illustrative cycle" intro="This is a teaching visual, not a performance claim. Move the slider to explore the tactical band." />
        <Reveal><DAAEViz /></Reveal>
      </Section>

      <Section cream>
        <SectionHead label="The engine, in four moves" title="How DAAE works" />
        <NumberedList items={framework} />
      </Section>

      <Section>
        <div className="max-w-3xl"><SectionHead label="FAQ" title="What DAAE is — and isn't" /><FAQ items={faq} /></div>
      </Section>

      <Section dark>
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <SectionHead label="Next step" title="Explore DAAE for your portfolio." dark intro="We'll explain the rules, the band and the risks in plain language — and whether it suits your plan." />
          <div className="bg-white/5 border border-white/10 p-8 md:p-10"><LeadForm dark interest="DAAE — Dynamic Asset Allocation Engine" title="Request a DAAE walkthrough" /></div>
        </div>
      </Section>
    </div>
  );
}
